import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  type AllSportsItems,
  type BaseNavItem,
  type ItemsType,
  type NavItemNoIcon,
  navItemsType,
  navItemType,
  type SecondLevelNavItem,
  type SiteConfig,
  type SubLink,
  type SublinkItem,
  variant,
} from '@cx-web/domain-types';
import classNames from 'classnames';
import { useOnClickOutside } from 'usehooks-ts';

import Icon from '../../../../primitives/icon/index';
import { mergePersistentParams } from '../../utils/merge-persistent-params';
import CustomDropdown from '../CustomDropdown/CustomDropdown';
import { HeaderAllSports } from '../HeaderAllSports/index';
import PromoCodeDropdown from '../HeaderMobile/sub-components/Menu/PromoCodeDropdown';
import SplitNavLinks from '../HeaderMobile/sub-components/SplitNavLinks/index';
import { SecondaryAllSports } from '../SecondaryAllSports/index';

import styles from './HeaderNavItem.module.css';

type HeaderNavItemExtras = {
  bookmakers: SiteConfig['bookmakers'];
  isUkStyle?: boolean;
  serverUrl: string;
};

type HeaderNavItem =
  | (BaseNavItem &
      HeaderNavItemExtras & {
        dropdownRef?: React.RefObject<HTMLDivElement | null> | null;
        items?: AllSportsItems | NavItemNoIcon[] | SublinkItem[];
        itemsType?: ItemsType;
        labelPill?: string;
        subLinks?: SubLink[];
        variant: 'primary';
      })
  | (SecondLevelNavItem &
      HeaderNavItemExtras & {
        dropdownRef?: React.RefObject<HTMLDivElement | null> | null;
        items?: AllSportsItems | NavItemNoIcon[] | SublinkItem[];
        itemsType?: ItemsType;
        labelPill?: string;
        subLinks?: SubLink[];
        variant: 'secondary';
      })
  | (NavItemNoIcon &
      HeaderNavItemExtras & {
        dropdownRef?: React.RefObject<HTMLDivElement | null> | null;
        itemsType?: ItemsType;
        subLinks?: SubLink[];
        variant: 'tertiary';
      });

interface NavLinkProps extends SecondLevelNavItem {
  'aria-label'?: string;
  isUkStyle?: boolean;
  labelPill?: string;
  serverUrl?: string;
  variant: HeaderNavItem['variant'];
}

interface LinkIconProps {
  icon: SecondLevelNavItem['icon'];
  isUkStyle?: boolean;
  variant: HeaderNavItem['variant'];
}

const LinkIcon = (props: LinkIconProps) => {
  if (props.variant === variant.secondary) {
    const { icon, isUkStyle } = props;
    // Design specifies 20×20; legacy secondary stays 22×22.
    const size = isUkStyle ? 20 : 22;

    return icon ? <Icon className={styles.icon} height={size} icon={icon} width={size} /> : null;
  }

  return null;
};

const NavLink = ({
  'aria-label': ariaLabel,
  icon,
  isUkStyle,
  labelPill,
  link,
  name,
  serverUrl,
  variant,
}: NavLinkProps) => {
  const requestUrl = serverUrl;
  const showPill = Boolean(isUkStyle && labelPill && (variant === 'primary' || variant === 'tertiary'));

  // TODO added by Weili: this needs to be revisited as the UI code shouldn't be coupled with deployment environment. This should come from server-side
  // the link is internal when the url is oddschecker.com, occloud.io, localhost, or a subdomain of oddschecker.com, or is a relative path
  const isInternal =
    link?.includes('oddschecker.com') ||
    link?.includes('occloud.io') ||
    link?.includes('localhost') ||
    link?.startsWith('/') ||
    link?.startsWith('#');

  return (
    <a
      aria-label={showPill ? `${ariaLabel} (${labelPill})` : ariaLabel}
      className={styles.item}
      data-nav-target={link}
      data-testid="nav-link"
      href={mergePersistentParams(link || '', requestUrl)}
      rel={isInternal ? undefined : 'nofollow'}
    >
      <LinkIcon icon={icon} isUkStyle={isUkStyle} variant={variant} />
      {name}
      {showPill && (
        <span aria-hidden="true" className={styles.labelPill}>
          {labelPill}
        </span>
      )}
    </a>
  );
};

interface NavExpandableProps extends SecondLevelNavItem {
  dropdownRef?: React.RefObject<HTMLDivElement | null> | null;
  isUkStyle?: boolean;
  items: AllSportsItems;
  serverUrl?: string;
  variant: HeaderNavItem['variant'];
}

const NavExpandable = (props: NavExpandableProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const { dropdownRef, isUkStyle = false, name, serverUrl } = props;
  const [isExpanded, setIsExpanded] = useState(false);

  const toggle = () => setIsExpanded(s => !s);
  const close = () => setIsExpanded(false);

  useOnClickOutside(ref as React.RefObject<HTMLElement>, event => {
    const target = event.target as Node | null;
    if (dropdownRef?.current && target && dropdownRef.current.contains(target)) {
      return;
    }
    close();
  });

  const renderDropdown = () => {
    if (!isExpanded) return null;

    const dropdown = isUkStyle ? (
      <SecondaryAllSports items={props.items} name={name ?? ''} serverUrl={serverUrl} />
    ) : (
      <HeaderAllSports items={props.items!} onClose={close} serverUrl={serverUrl} />
    );

    if (dropdownRef?.current) {
      return createPortal(dropdown, dropdownRef.current);
    }
    return dropdown;
  };

  return (
    <>
      <div className={styles.navExpandableWrapper} data-testid="nav-expandable" ref={ref}>
        <button className={styles.odds} data-testid="nav-expandable-hamburger" onClick={toggle} type="button">
          {!isUkStyle && <Icon height={14} icon="HamburgerMenu" width={14} />}
          {name}
          {isUkStyle && (
            <span
              className={classNames(styles.arrow, isExpanded ? styles.arrowOpen : styles.arrowClose)}
              data-testid="nav-expandable-chevron"
            />
          )}
        </button>
      </div>
      {renderDropdown()}
    </>
  );
};

const SubLinks = ({
  isUkStyle = false,
  parentLink,
  parentLinkText,
  serverUrl,
  subLinks,
}: {
  isUkStyle?: boolean;
  parentLink?: string;
  parentLinkText?: string;
  serverUrl: string;
  subLinks: SubLink[];
}) => {
  const requestUrl = serverUrl;
  const selectedPath = new URL(serverUrl).pathname;

  return (
    <div className={styles.moreNavLinks}>
      {subLinks.map(({ backgroundColour, iconRound, link = '', name }) => {
        const isSelected = link === selectedPath;

        return (
          <div className={styles.linkWrapper} key={name}>
            <a
              className={`${styles.item} ${styles.dropdownItem} ${isSelected ? styles.selected : ''}`}
              href={mergePersistentParams(link, requestUrl)}
            >
              {isUkStyle && iconRound && (
                <span
                  className={styles.sublinkIcon}
                  data-testid="sublink-icon"
                  style={backgroundColour ? { backgroundColor: backgroundColour } : undefined}
                >
                  <img alt="" src={iconRound} />
                </span>
              )}
              {name}
            </a>
          </div>
        );
      })}
      {isUkStyle && parentLink && (
        <a
          className={styles.viewAllLink}
          data-testid="sublink-view-all"
          href={mergePersistentParams(parentLink, requestUrl)}
        >
          {parentLinkText || 'View all'}
        </a>
      )}
    </div>
  );
};

export const HeaderNavItem: React.FC<HeaderNavItem> = props => {
  const {
    bookmakers,
    dropdownRef = null,
    isUkStyle = false,
    itemsType = '',
    labelPill,
    link = '',
    name = '',
    serverUrl,
    subLinks = [],
    type,
    variant,
  } = props;
  const selectedPath = new URL(serverUrl).pathname;
  const isSelected =
    link === selectedPath || (isUkStyle && variant === 'tertiary' && Boolean((props as NavItemNoIcon).isActive));

  const renderContent = () => {
    switch (type) {
      case navItemType.ALL_SPORTS_EXPANDABLE:
        return name ? (
          <NavExpandable
            dropdownRef={dropdownRef}
            icon={props.icon}
            isUkStyle={isUkStyle}
            items={props.items as AllSportsItems}
            link={link}
            name={name}
            serverUrl={serverUrl}
            type={type}
            variant={variant}
          />
        ) : null;

      case navItemType.FLAT_ITEMS_EXPANDABLE:
        if (variant === 'tertiary' && itemsType === navItemsType.PROMO_CODE && dropdownRef) {
          return (
            <CustomDropdown dropdownRef={dropdownRef} key={name} label={name}>
              <PromoCodeDropdown
                bookmakers={bookmakers}
                label={name}
                links={props.items as NavItemNoIcon[]}
                serverUrl={serverUrl}
              />
            </CustomDropdown>
          );
        }
        return null;

      case navItemType.LINK_GROUP:
        if (variant === 'tertiary' && dropdownRef) {
          const groupItem = props as NavItemNoIcon;
          return (
            <CustomDropdown dropdownRef={dropdownRef} isUkStyle={isUkStyle} key={name} label={name} variant={variant}>
              <SubLinks
                isUkStyle={isUkStyle}
                parentLink={groupItem.parentLink}
                parentLinkText={groupItem.parentLinkText}
                serverUrl={serverUrl}
                subLinks={subLinks}
              />
            </CustomDropdown>
          );
        }
        return null;

      case navItemType.SPLIT_EXPANDABLE:
        if ((variant === 'tertiary' || variant === 'secondary') && dropdownRef && props.groups) {
          return (
            <CustomDropdown dropdownRef={dropdownRef} isUkStyle={isUkStyle} key={name} label={name} variant={variant}>
              <SplitNavLinks isUkStyle={isUkStyle} links={props.groups} serverUrl={serverUrl} variant={variant} />
            </CustomDropdown>
          );
        }
        return null;

      case navItemType.LINK:
      default: {
        return name && type && variant ? (
          <NavLink
            aria-label={name}
            icon={props.icon}
            isUkStyle={isUkStyle}
            labelPill={labelPill}
            link={link}
            name={name}
            serverUrl={serverUrl}
            type={type}
            variant={variant}
          />
        ) : null;
      }
    }
  };

  return (
    <li
      className={classNames(
        styles[variant],
        styles.itemWrapper,
        isUkStyle && (variant === 'primary' || variant === 'secondary' || variant === 'tertiary') && styles.uk,
        isSelected && styles.selected,
      )}
      data-nav-selected={isSelected ? 'true' : undefined}
      data-testid="header-nav-item"
      key={link}
    >
      {renderContent()}
    </li>
  );
};
